import { useAuth, useUser } from '@clerk/react-router';
import CT from '@marketplace-ai/tags';
import { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { UserContextV2 } from '~/app';
import hospitalImage from '~/assets/hospital.jpg';
import providerImage from '~/assets/provider.jpg';
import { Button } from '~/components';
import { buildRegistrationPath, readRegistrationReturnTo } from '~/routing/registrationReturnTo';
import { paths } from '~/routing/routes';
import { clearPendingRole, takePendingRole } from './pendingRoleIntent';

/**
 * Role Selection Page
 *
 * Shared page for new users to choose their path:
 * - Healthcare Provider (MedCredVault + HealthcareLX)
 * - Employer/Facility (HealthcareLX only)
 */

export const RoleSelectPage = () => {
  const actorRef = UserContextV2.useActorRef();
  const { signOut } = useAuth();
  const { user, isLoaded } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const returnTo = readRegistrationReturnTo(location.search);

  /**
   * [MCV-1591] [MCV-1666] The role is chosen BEFORE signup -- on
   * LoginOrCreateAccountPage, carried through Clerk via `setPendingRole` on
   * SignInPage -- but Clerk does not carry that choice across: a fresh
   * account always lands the user machine in $_CHOOSE_ROLE, and
   * UserStateRouter routes $_CHOOSE_ROLE here regardless of where signup
   * started. Without this the user would pick a role, sign up, and be asked
   * to pick again.
   *
   * Consumption is bound to the ACCOUNT-CREATION ORDER, not just to storage
   * scope.
   * sessionStorage alone was not enough: the value outlives the signup attempt
   * within the tab, so choosing a role, abandoning signup, then signing in as a
   * different roleless account would apply the abandoned choice to that
   * account -- and there is no UI that sends ON_BACK_TO_ROLE_SELECT, so the
   * only escape is signing out. The satellite hop also writes one copy per
   * origin (HLX and primary) and only the HLX one is consumed, leaving the
   * other behind; a read-side check neutralises both at once, which no
   * clear-on-sign-in hook could.
   *
   * Clerk creates the User at sign-up completion, so on the path this exists
   * for `createdAt` lands after the choice. Any pre-existing account fails the
   * check no matter how recently it was created, and is asked once -- the safe
   * direction.
   *
   * The page still renders normally when there is no usable choice: OAuth
   * sign-ups, the $_RESOLVING_USER_INFO recovery path, and anyone who reaches
   * $_CHOOSE_ROLE some other way all still get asked.
   */
  useEffect(() => {
    // Wait for Clerk: consuming before `createdAt` is knowable would apply the
    // intent without the check below ever running.
    if (!isLoaded) return;

    // Consumed unconditionally, even when it is not acted on -- a value that
    // fails the freshness check is stale by definition and must not survive to
    // be re-examined on a later visit.
    const pendingRole = takePendingRole();
    if (!pendingRole) return;

    const createdAt = user?.createdAt?.getTime();
    if (createdAt === undefined) return;

    // The account must have been created STRICTLY AFTER the choice was made.
    // An account that predates the choice cannot be the one it was for, and an
    // equal timestamp does not prove ordering either -- so it is refused too,
    // for the same fail-closed reason.
    //
    // No skew tolerance, deliberately. `chosenAt` is the browser clock and
    // `createdAt` is Clerk's, so ordering near the boundary is genuinely
    // uncertain -- and the two failure directions are not symmetric. Guessing
    // wrong in favour of applying drops the user into a registration flow with
    // no picker to escape from (nothing sends ON_BACK_TO_ROLE_SELECT); guessing
    // wrong the other way asks them to choose again. Uncertain ordering fails
    // closed.
    if (createdAt <= pendingRole.chosenAt) return;

    const targetPath =
      pendingRole.role === 'EMPLOYER' ? paths.register.employer : paths.register.provider;

    // Send the same events the corresponding button sends, then land on the
    // same route it would navigate to.
    actorRef.send({ type: 'ON_BACK_TO_ROLE_SELECT' });
    actorRef.send({
      type:
        pendingRole.role === 'EMPLOYER'
          ? 'ON_START_EMPLOYER_REGISTRATION'
          : 'ON_START_PROVIDER_REGISTRATION',
    });
    navigate(buildRegistrationPath(targetPath, returnTo));
    // `takePendingRole` clears the stored intent on read, so a re-run of this
    // effect (e.g. `returnTo` changing) is a safe no-op -- there is nothing
    // left to consume.
  }, [actorRef, isLoaded, user, navigate, returnTo]);

  const handleCancel = async () => {
    // Drop any pending choice too -- signing out here means backing all the
    // way out, and a leftover value would pre-answer the next signup.
    clearPendingRole();
    await signOut({ redirectUrl: paths.landing });
    // State machine will reset to anonymous via ON_USER_LOGOUT
  };

  const handleStartProviderRegistration = () => {
    actorRef.send({ type: 'ON_BACK_TO_ROLE_SELECT' });
    actorRef.send({ type: 'ON_START_PROVIDER_REGISTRATION' });
  };
  const handleStartEmployerRegistration = () => {
    actorRef.send({ type: 'ON_BACK_TO_ROLE_SELECT' });
    actorRef.send({ type: 'ON_START_EMPLOYER_REGISTRATION' });
  };

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center bg-white px-4 py-20 sm:px-6">
      <div className="absolute top-4 right-4">
        <Button onClick={handleCancel} variant="bar" colorVariant="secondary">
          Sign Out
        </Button>
      </div>
      <h1 className="heading-large mb-4 text-center">Select Your Role</h1>
      <div className="grid w-full max-w-[732px] grid-cols-1 gap-8 md:grid-cols-2">
        <div className="flex min-w-0 w-full flex-col items-center justify-center">
          <Link
            className="block w-full"
            to={buildRegistrationPath(paths.register.provider, returnTo)}
            onClick={handleStartProviderRegistration}
          >
            <img
              src={providerImage}
              alt="provider"
              className="mb-3 aspect-[7/11] w-full cursor-pointer rounded-lg object-cover"
            />
          </Link>
          <Button
            cyTag={CT.UserTypeSelect.providerButton}
            variant="bar"
            type="button"
            onClick={() => {
              handleStartProviderRegistration();
              navigate(buildRegistrationPath(paths.register.provider, returnTo));
            }}
          >
            I'm a Provider
          </Button>
        </div>
        <div className="flex min-w-0 w-full flex-col items-center justify-center">
          <Link
            className="block w-full"
            to={buildRegistrationPath(paths.register.employer, returnTo)}
            onClick={handleStartEmployerRegistration}
          >
            <img
              src={hospitalImage}
              alt="hospital"
              className="mb-3 aspect-[7/11] w-full cursor-pointer rounded-lg object-cover"
            />
          </Link>
          <Button
            cyTag={CT.UserTypeSelect.employerButton}
            variant="bar"
            colorVariant="secondary"
            type="button"
            onClick={() => {
              handleStartEmployerRegistration();
              navigate(buildRegistrationPath(paths.register.employer, returnTo));
            }}
          >
            I'm a Facility
          </Button>
        </div>
      </div>
    </div>
  );
};
